import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useBlockedWords, useRemoveBlockedWords } from '../api';
import { AddBlockedWordsDialog } from '../components/AddBlockedWordsDialog';

interface BlockedWordRow {
  word: string;
}

/**
 * Confidential words are masked. The real word is rendered only while the
 * cell is hovered / focused, so it is not sitting in the DOM otherwise.
 */
function MaskedWord({ word }: { word: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex h-6 w-40 cursor-pointer items-center gap-2 whitespace-nowrap text-left font-mono text-sm"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-label="View word"
    >
      {visible ? (
        <span>{word}</span>
      ) : (
        <>
          <span className="select-none tracking-widest text-muted-foreground">
            ••••••••
          </span>
          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
        </>
      )}
    </button>
  );
}

export function BlockedWordsPage() {
  const blockedWords = useBlockedWords();
  const removeBlockedWords = useRemoveBlockedWords();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deletingWord, setDeletingWord] = useState<string | null>(null);

  const rows: BlockedWordRow[] = useMemo(
    () => (blockedWords.data?.words ?? []).map((w) => ({ word: w })),
    [blockedWords.data],
  );

  const handleDeleteWord = (word: string) => {
    setDeletingWord(word);
    removeBlockedWords.mutate([word], {
      onSuccess: () => {
        toast.success('Word removed from blocked words.');
        setDeletingWord(null);
      },
      onError: () => {
        toast.error('Could not remove the word. Try again.');
        setDeletingWord(null);
      },
    });
  };

  const columns = useMemo<ColumnDef<BlockedWordRow>[]>(
    () => [
      {
        id: 'index',
        header: 'Sr No',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.index + 1}
          </span>
        ),
      },
      {
        accessorKey: 'word',
        header: 'Word',
        cell: ({ row }) => <MaskedWord word={row.original.word} />,
      },
      {
        id: 'actions',
        header: 'Action',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => {
          const isDeleting = deletingWord === row.original.word;
          return (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={deletingWord !== null}
              onClick={() => handleDeleteWord(row.original.word)}
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          );
        },
      },
    ],
    [deletingWord],
  );

  return (
    <FillPage>
      <PageHeader
        title="Blocked Words"
        description="Manage blocked words that users cannot use in TTS text or TTS variable content."
        actions={
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Words
          </Button>
        }
      />

      <DataTable<BlockedWordRow, unknown>
        tableId="blocked-words:list:v1"
        columns={columns}
        data={rows}
        isLoading={blockedWords.isLoading}
        searchPlaceholder="Search blocked words…"
        emptyTitle="No blocked words"
        emptyDescription="Add words that should be blocked in TTS content."
        getRowId={(row) => row.word}
        pageSize={25}
        disableExport
      />

      <AddBlockedWordsDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
    </FillPage>
  );
}
